<script setup lang="ts">
import type { CommunicationLogEntry } from "@/components/pages/outlet/crm/communication_logs/communicationLog";
import { EnumCustomerHappinessRating } from "@/app/services/generated/enums/enumCustomerHappinessRating.generated";
import type CommunicationTypesCache from "@/app/caching/caches/communicationTypesCache";
import type CommunicationSubjectsCache from "@/app/caching/caches/communicationSubjectsCache";

interface CommunicationLogControlProps {
  value: CommunicationLogEntry;
  cache: {
    communicationTypes: CommunicationTypesCache;
    communicationSubjects: CommunicationSubjectsCache;
  };
}

interface CommunicationLogControlEvents {
  (event: "update:value", value: CommunicationLogEntry): void;
}

const props = defineProps<CommunicationLogControlProps>();
const emit = defineEmits<CommunicationLogControlEvents>();

// Inline copy of BubbleDeepEvent because this uses a custom clone function, not a generated one.
const bubbleDeepEvent = (
  updateObjectFn: (obj: CommunicationLogEntry) => void
) => {
  const clonedObject = props.value.clone();
  updateObjectFn(clonedObject);
  emit("update:value", clonedObject);
};
</script>

<template>
  <v-container fluid pa-0>
    <slot name="prepend" />

    <v-row>
      <v-col cols="6">
        <bm-dropdown
          :value="value.communicationTypeId"
          :t="$m.keys.crm_communication_type"
          :items="cache.communicationTypes.all"
          :sort-items="false"
          item-text="name"
          item-value="id"
          :rules="[$ruleSet.required()]"
          @update:value="
            bubbleDeepEvent((log) => (log.communicationTypeId = $event))
          "
        />
      </v-col>
      <v-col cols="6">
        <bm-dropdown
          :value="value.communicationSubjectId"
          :t="$m.keys.crm_subject"
          :items="cache.communicationSubjects.all"
          :sort-items="false"
          item-text="name"
          item-value="id"
          :rules="[$ruleSet.required()]"
          @update:value="
            bubbleDeepEvent((log) => (log.communicationSubjectId = $event))
          "
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="6">
        <bm-date-time
          :value="value.timestamp"
          :t="$m.keys.crm_timestamp"
          :rules="[$ruleSet.required()]"
          @update:value="bubbleDeepEvent((log) => (log.timestamp = $event))"
        />
      </v-col>
      <v-col cols="6">
        <bm-enum-ddl
          :value="value.customerHappinessRating"
          :t="$m.keys.crm_customer_happiness_rating"
          :enum-type="EnumCustomerHappinessRating"
          :rules="[$ruleSet.required()]"
          @update:value="
            bubbleDeepEvent((log) => (log.customerHappinessRating = $event))
          "
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <bm-text-area
          :value="value.description"
          :t="$m.keys.description"
          :rules="[$ruleSet.required()]"
          @update:value="bubbleDeepEvent((log) => (log.description = $event))"
        />
      </v-col>
    </v-row>

    <slot name="append" />
  </v-container>
</template>
